import {
  Button,
  ButtonDesigns,
  ButtonTypes,
  getFontCss,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useSelector } from 'react-redux'
import {
  selectPostingVirtualCardFromState,
  selectTopLevelErrorFromState,
} from 'src/app/CreateVirtualCardForm/redux/createVirtualCardForm.selectors'
import {
  selectPostingOrganizationInvitesFromState,
  selectPostInvitesTopLevelErrorFromState,
} from 'src/app/OrganizationInvites/redux/organizationInvites.selectors'
import ErrorList from 'src/components/ErrorList/ErrorList'
import { OrganizationRoles } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { css } from 'styled-components'

interface CreateVirtualCardRecipientWarningPopupProps {
  selectedEmailsNotInOrg: string[]
  submitFunction: (role?: OrganizationRoles) => void
  closeFunction: (val: boolean) => void
}

const CreateVirtualCardRecipientWarningPopup: React.FC<
  CreateVirtualCardRecipientWarningPopupProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const postingVirtualCard = useSelector(selectPostingVirtualCardFromState)
  const postingOrganizationInvites = useSelector(
    selectPostingOrganizationInvitesFromState,
  )
  const topLevelError = useSelector(selectTopLevelErrorFromState)
  const postInvitesTopLevelError = useSelector(
    selectPostInvitesTopLevelErrorFromState,
  )
  return (
    <Popup
      css={css`
        display: flex;
        flex-direction: column;
        z-index: 1;
      `}
      onClickClose={(): void => {
        props.closeFunction(false)
      }}
      showCloseButton
    >
      <div
        css={css`
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          ${getFontCss(
            40,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        {translate('Are you sure?')}
      </div>
      <div
        css={css`
          margin-top: 32px;
          display: inline;
          ${getFontCss(
            16,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.regular,
          )};
        `}
      >
        <span
          css={css`
            flex-direction: column;
          `}
        >
          {translate(
            '${number} virtual card recipient(s) are not in your org.',
            {
              number: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )}
                  `}
                >
                  {props.selectedEmailsNotInOrg.length.toString() ?? '0'}
                </span>
              ),
            },
          )}
          <div
            css={css`
              display: flex;
              margin-top: 10px;
            `}
          >
            {translate(
              'By selecting "Add Guest(s) & Create Card(s)," they will be added as Guest(s) in addition to receiving their virtual card(s).',
            )}
          </div>
        </span>
      </div>
      <div
        css={css`
          margin-top: 32px;
          border: 1px solid ${styleConstants.colors.gray_medium};
          border-radius: 10px;
          background-color: ${styleConstants.colors.gray_light};
          overflow-y: auto;
          max-height: 225px;
        `}
      >
        <div
          css={css`
            padding: 10px 0px;
          `}
        >
          {props.selectedEmailsNotInOrg.map((email, i) => (
            <div
              key={i}
              css={css`
                display: flex;
                padding: 10px;
                align-items: center;
              `}
            >
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  margin-left: 10px;
                  overflow: hidden;
                `}
              >
                <span
                  id={email}
                  css={css`
                    overflow: hidden;
                    text-overflow: ellipsis;
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.regular,
                    )}
                  `}
                >
                  {email}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {(topLevelError || postInvitesTopLevelError) !== undefined && (
        <ErrorList errors={[topLevelError || postInvitesTopLevelError || '']} />
      )}
      <div
        css={css`
          display: flex;
          align-items: center;
          margin-top: 32px;
          gap: 20px;
        `}
      >
        <Button
          type={ButtonTypes.submit}
          design={ButtonDesigns.designCta}
          big
          text={translate('Add Guest(s) & Create Card(s)')}
          onClick={(): void => props.submitFunction(OrganizationRoles.guest)}
          loading={postingVirtualCard || postingOrganizationInvites}
        />
        <span
          role="link"
          tabIndex={0}
          onClick={(): void => {
            if (!(postingVirtualCard || postingOrganizationInvites))
              props.closeFunction(false)
          }}
          onKeyDown={undefined}
          // TODO: create a hoverable link design for the red styling
          css={css`
            ${postingVirtualCard || postingOrganizationInvites
              ? css`
                  ${getFontCss(
                    14,
                    styleConstants.colors.red_light,
                    styleConstants.fontFamilies.medium,
                  )};
                `
              : css`
                  ${getFontCss(
                    14,
                    styleConstants.colors.red,
                    styleConstants.fontFamilies.medium,
                  )};
                `}

            &:hover {
              ${!(postingVirtualCard || postingOrganizationInvites) &&
              css`
                text-decoration: underline;
                cursor: pointer;
              `}
            }
          `}
        >
          {translate('No, let me change recipients')}
        </span>
      </div>
    </Popup>
  )
}

export default CreateVirtualCardRecipientWarningPopup
